
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';

export const exportFlowToPdf = async ({
    nodes,
    reactFlowWrapper,
    fileName,
    backgroundColor = '#ffffff',
    rowsPerPage = 4,
    rowGap = 150,
    padding = 100,
    minHeight = 400,
    pixelRatio = 2.5,
    onProgress,
    onError,
    onSuccess,
}) => {
    const flowElement = reactFlowWrapper.current;
    if (!flowElement) {
        onError?.('Flow element not found');
        return;
    }

    try {
        onProgress?.('Preparing PDF download...', 'info');

        // Hide controls and attribution
        const controls = flowElement.querySelector('.react-flow__controls');
        const attribution = flowElement.querySelector('.react-flow__attribution');
        if (controls) controls.style.display = 'none';
        if (attribution) attribution.style.display = 'none';

        // 1. Sort nodes by Y position
        const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);

        // 2. Create rows with grouping logic
        const rows = [];
        let currentRow = [];
        let currentRowMinY = sortedNodes[0]?.position.y || 0;
        let currentRowMaxY = currentRowMinY;

        sortedNodes.forEach((node) => {
            const nodeHeight = node.height || 80;
            const nodeY = node.position.y;
            const nodeBottomY = nodeY + nodeHeight;

            // Check if this node belongs to current row
            if (currentRow.length === 0 || Math.abs(nodeY - currentRowMinY) < rowGap) {
                currentRow.push(node);
                currentRowMinY = Math.min(currentRowMinY, nodeY);
                currentRowMaxY = Math.max(currentRowMaxY, nodeBottomY);
            } else {
                // Start new row
                rows.push({
                    nodes: currentRow,
                    minY: currentRowMinY,
                    maxY: currentRowMaxY,
                });
                currentRow = [node];
                currentRowMinY = nodeY;
                currentRowMaxY = nodeBottomY;
            }
        });

        // Push last row
        if (currentRow.length) {
            rows.push({
                nodes: currentRow,
                minY: currentRowMinY,
                maxY: currentRowMaxY,
            });
        }

        // 3. Configuration
        const totalPages = Math.ceil(rows.length / rowsPerPage);

        console.log('Total rows:', rows.length);
        console.log('Total pages:', totalPages);

        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'px',
            format: 'a4',
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const viewport = flowElement.querySelector('.react-flow__viewport');
        const originalTransform = viewport.style.transform;

        // 4. Loop through pages
        for (let p = 0; p < totalPages; p++) {
            const startRowIdx = p * rowsPerPage;
            const endRowIdx = Math.min((p + 1) * rowsPerPage, rows.length);
            const pageRows = rows.slice(startRowIdx, endRowIdx);

            // Calculate bounds for this page's rows
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;

            pageRows.forEach((row) => {
                row.nodes.forEach((node) => {
                    const w = node.width || 200;
                    const h = node.height || 80;
                    const x = node.position.x;
                    const y = node.position.y;

                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x + w);
                    maxY = Math.max(maxY, y + h);
                });
            });

            // Add padding
            minX -= padding;
            minY -= padding;
            maxX += padding;
            maxY += padding;

            // Ensure minimum height to avoid cutting
            if (maxY - minY < minHeight) {
                const diff = minHeight - (maxY - minY);
                maxY += diff / 2;
                minY -= diff / 2;
            }

            // Move viewport to capture this region
            viewport.style.transform = `translate(${-minX}px, ${-minY}px) scale(1)`;

            // Wait for transform to apply
            await new Promise((r) => setTimeout(r, 500));

            // Capture this area
            const dataUrl = await toPng(viewport, {
                backgroundColor: backgroundColor,
                pixelRatio: pixelRatio,
                width: maxX - minX,
                height: maxY - minY,
                cacheBust: true,
                quality: 1.0,
            });

            // Add page if not first
            if (p > 0) pdf.addPage();

            // Calculate scaling to fit PDF page
            const imgW = maxX - minX;
            const imgH = maxY - minY;
            const ratio = Math.min(pdfWidth / imgW, pdfHeight / imgH) * 0.95;

            const finalW = imgW * ratio;
            const finalH = imgH * ratio;

            // Center on page
            const x = (pdfWidth - finalW) / 2;
            const y = (pdfHeight - finalH) / 2;

            pdf.addImage(dataUrl, 'PNG', x, y, finalW, finalH);

            console.log(
                `Page ${p + 1}/${totalPages} captured - Rows: ${startRowIdx + 1} to ${endRowIdx}`
            );

            // Report progress
            onProgress?.(
                `Processing page ${p + 1} of ${totalPages}...`,
                'info'
            );
        }

        // Restore original viewport
        viewport.style.transform = originalTransform;

        // Restore controls
        if (controls) controls.style.display = '';
        if (attribution) attribution.style.display = '';

        // Save PDF
        const timestamp = new Date().toISOString().split('T')[0];
        const finalFileName = fileName || `export-${timestamp}.pdf`;
        pdf.save(finalFileName);

        onSuccess?.(
            `✅ PDF exported successfully (${totalPages} pages)`,
            'success'
        );

        return { success: true, pages: totalPages };
    } catch (err) {
        console.error('PDF Export Error:', err);
        onError?.('❌ Error exporting PDF. Please try again.', 'error');
        return { success: false, error: err };
    }
};